import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Icon } from "@/components/ui/Icon";
import { useTranslation } from "react-i18next";
import { useDeleteNote } from "../../hooks/useNotes";

interface LeaveNoteDialogProps {
  noteId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `shared_note_left {source}`. */
  source: string;
  /** After leaving (navigate away from the note, close a sheet). */
  onLeft?: () => void;
}

/**
 * "Leave this note?" for a note shared with this account. Leaving is a
 * member's delete (PLAN.md D5), so it goes through `useDeleteNote`, which
 * leaves instead of trashing; it works offline (a queued `delete`).
 */
export function LeaveNoteDialog({ noteId, open, onOpenChange, source, onLeft }: LeaveNoteDialogProps) {
  const { t } = useTranslation();
  const deleteNote = useDeleteNote();
  const leave = () => {
    onOpenChange(false);
    // Let the dialog close before the route changes (see NoteHeaderPill).
    setTimeout(() => {
      void deleteNote.mutate(noteId, source);
      onLeft?.();
    }, 0);
  };
  return (
    <Dialog alert open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            <Icon name="sign-out" size={14} />
            {t("sharing.leaveConfirmTitle")}
          </DialogTitle>
        </DialogHeader>
        <p className="px-4 py-4 text-sm text-ink-secondary">{t("sharing.leaveConfirmBody")}</p>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button variant="danger" size="sm" onClick={leave}>
            {t("sharing.leave")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
